import React, { isValidElement, useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import getTextContent from '../../utils/getTextContent'
import { Video } from '../Video/Video'
import { PlayButton } from '../PlayButton/PlayButton'

/**
 * Bronson HeroTeaser component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-hero-teaser.html
 *
 * @param {boolean} [auto] - Render the `auto` variant.
 * @param {React.ReactNode} [bar] - Include the `.c-hero-teaser__bar` element.
 * @param {boolean} [bottomCenter] - Pin the HeroTeaser image to `bottom center`.
 * @param {boolean} [bottomLeft] - Pin the HeroTeaser image to the `bottom left`.
 * @param {boolean} [bottomRight] - Pin the HeroTeaser image to the `bottom right`.
 * @param {React.ReactNode} [box] - Render a box instead of the title.
 * @param {boolean} [buttonIsHiddenOnLargeScreen] - Hide the HeroTeaser button on large viewports.
 * @param {object} [buttonProps] - Additional button properties.
 * @param {string} [buttonText] - The button label.
 * @param {boolean} [centerLeft] - Pin the HeroTeaser image to the `center left`.
 * @param {boolean} [centerRight] - Pin the HeroTeaser image to the `center right`.
 * @param {React.ReactNode} [children] - Additional component children.
 * @param {string} [className] - Additional class names for the main component element.
 * @param {{alt?: string, sizes?: string, src?: string, srcSet?: string}} [imgProps] - Additional HeroTeaser image props to set, e.g. `[srcset]` or `[sizes]`.
 * @param {boolean} [insideWrapper] - Unused in Bronson-React as `imgProps` are set externally.
 * @param {boolean} [inverted] - Render the inverted variant.
 * @param {boolean} [noImage] - Render no image.
 * @param {function} [onButtonClick] - Callback function for the HeroTeaser button.
 * @param {React.ReactNode} [pretitle] - Content for the HeroTeaser pretitle.
 * @param {boolean} [shallow] - Render the `Shallow Height` variant.
 * @param {React.ReactNode} [sub] - Content for the HeroTeaser subtitle.
 * @param {string|React.ReactNode|React.ReactNodeArray} [title] - Content for the HeroTeaser title.
 * @param {boolean} [topCenter] - Pin the HeroTeaser image to the `top center`.
 * @param {boolean} [topLeft] - Pin the HeroTeaser image to the `top left`.
 * @param {boolean} [topRight] - Pin the HeroTeaser image to the `top right`.
 * @param {object} [video] - Render background video variant.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The HeroTeaser component.
 * @constructor
 */
export function HeroTeaser({
  auto,
  bar,
  bottomCenter,
  bottomLeft,
  bottomRight,
  box,
  buttonIsHiddenOnLargeScreen,
  buttonProps,
  buttonText,
  centerLeft,
  centerRight,
  children,
  className,
  imgProps,
  insideWrapper,
  inverted,
  noImage,
  onButtonClick,
  pretitle,
  shallow,
  sub,
  title,
  topCenter,
  topLeft,
  topRight,
  video,
  testId,
  ...otherProps
}) {
  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef()

  /**
   * Handle custom {@link PlayButton} interactions.
   * @type {(function(): (*|undefined))|*}
   */
  const onPlayButtonClick = useCallback(() => {
    if (videoRef?.current) {
      return isPlaying ? videoRef?.current?.pause() : videoRef?.current?.play()
    }
    return () => {}
  }, [isPlaying, videoRef])

  /**
   * Handle `onPlay`, `onStop` and `onPause` events on the {@link Video} instance.
   * We cannot pass `setIsPlaying` directly into the video handlers as that
   * resolves in re-renders as the {@link Video} component does not memoize the invocations.
   * @type {(function(*): void)|*}
   */
  const onVideoToggle = useCallback(
    (plyr) => {
      setIsPlaying(!plyr?.media?.paused)
    },
    [setIsPlaying]
  )

  const divClassNameList = classNames(
    'c-hero-teaser',
    {
      // Main class modifier convenience prop extension.
      'c-hero-teaser--top-left': topLeft, // Convenience prop from Bronson variants.
      'c-hero-teaser--top-center': topCenter, // Convenience prop from Bronson variants.
      'c-hero-teaser--top-right': topRight, // Convenience prop from Bronson variants.
      'c-hero-teaser--center-left': centerLeft, // Convenience prop from Bronson variants.
      'c-hero-teaser--center-right': centerRight, // Convenience prop from Bronson variants.
      'c-hero-teaser--bottom-center': bottomCenter, // Convenience prop from Bronson variants.
      'c-hero-teaser--bottom-left': bottomLeft, // Convenience prop from Bronson variants.
      'c-hero-teaser--bottom-right': bottomRight, // Convenience prop from Bronson variants.
      'c-hero-teaser--shallow': shallow, // Convenience prop from Bronson variants.
      'c-hero-teaser--auto': auto, // Convenience prop from Bronson variants.
      'c-hero-teaser--inverted': inverted, // Convenience prop from Bronson variants.
      'c-hero-teaser--video': video && noImage, // Convenience prop from Bronson variants.
    },
    className
  ).trim()

  function renderHeroTeaserPretitle() {
    if (pretitle) {
      return (
        <div className="c-hero-teaser__pretitle">
          <p>{pretitle}</p>
        </div>
      )
    }
    return null
  }

  function renderHeroTeaserSub() {
    if (sub) {
      return (
        <div className="c-hero-teaser__subtitle">
          <p>{sub}</p>
        </div>
      )
    }
    return null
  }

  function renderHeroTeaserText() {
    if (children) {
      return (
        <div className="c-hero-teaser__text">
          <p>{children}</p>
        </div>
      )
    }
    return null
  }

  const buttonWrapperClassNameList = classNames('c-hero-teaser__button', {
    'c-hero-teaser__button--hidden-on-large-screen': buttonIsHiddenOnLargeScreen,
  }).trim()

  function renderHeroTeaserHasButton() {
    if (buttonText) {
      return (
        <div id="js-sticky-button" className={buttonWrapperClassNameList}>
          <button className="c-btn" onClick={onButtonClick} type="button" {...buttonProps}>
            <span className="c-btn__text">{buttonText}</span>
          </button>
        </div>
      )
    }
    return null
  }

  function renderHeroTeaserHasBar() {
    if (bar) {
      return (
        <div className="c-hero-teaser__bar">
          <div className="o-page-wrap">{bar}</div>
        </div>
      )
    }
    return null
  }

  function renderHeroTeaserImage() {
    /**
     * Handles fallback if {@link imgProps.alt} is not set.
     */
    const imgAttrs = {
      ...imgProps,
      ...{ alt: imgProps?.alt ?? getTextContent(title) ?? '' },
    }
    // eslint-disable-next-line jsx-a11y/alt-text
    return <img className="c-hero-teaser__image" {...imgAttrs} />
  }

  function renderTitle() {
    /**
     * If the title was an array, map over it
     * and wrap every item in `.c-hero-teaser__title-row`.
     */
    if (Array.isArray(title)) {
      return title.map((row, i) => (
        <span className="c-hero-teaser__title-row" key={`c-hero-teaser__title-row-${i}`}>
          {row}
        </span>
      ))
    }
    /**
     * If the title is a string or a valid React element,
     * just return it as a single child.
     */
    if (typeof title === 'string' || isValidElement(title)) {
      return <span className="c-hero-teaser__title-row">{title}</span>
    }
    return null
  }

  function renderHeroTeaserBoxOrTitle() {
    return box ? (
      <div className="c-hero-teaser__box">{box}</div>
    ) : (
      <header className="c-hero-teaser__heading">
        {renderHeroTeaserPretitle()}
        <div className="c-hero-teaser__title">
          <h1>{renderTitle()}</h1>
        </div>
        {renderHeroTeaserSub()}
        {renderHeroTeaserText()}
      </header>
    )
  }

  function renderBackgroundVideo() {
    return video ? (
      <div className="c-hero-teaser__background-video">
        <Video ref={videoRef} {...video} onPlay={onVideoToggle} onPause={onVideoToggle} />
        <PlayButton
          className={classNames('c-hero-teaser__background-video-control', 'js-background-video-control', {
            'is-paused': !isPlaying,
          })}
          iconClassName="c-hero-teaser__background-video-control-icon"
          iconViaCss
          label="Start or stop the video"
          onClick={onPlayButtonClick}
        />
      </div>
    ) : (
      <div className="c-hero-teaser__image" />
    )
  }

  return (
    <div {...otherProps} data-testid={testId} className={divClassNameList}>
      {noImage || video ? renderBackgroundVideo() : renderHeroTeaserImage()}
      <div id="js-hero-teaser-content" className="c-hero-teaser__content">
        <div className="c-hero-teaser__content-inner">
          {renderHeroTeaserBoxOrTitle()}
          {renderHeroTeaserHasButton()}
        </div>
      </div>
      {renderHeroTeaserHasBar()}
    </div>
  )
}

HeroTeaser.propTypes = {
  bar: PropTypes.node, // Bronson template: 'hero-teaser-has-bar'.
  box: PropTypes.node, // Bronson template: 'bronson-user-form'.
  buttonIsHiddenOnLargeScreen: PropTypes.bool, // Bronson template: 'hero-teaser-button-is-hidden-on-large-screen'.
  buttonProps: PropTypes.oneOfType([PropTypes.object]),
  buttonText: PropTypes.string, // Bronson template: 'hero-teaser-button-text'.
  children: PropTypes.node, // Bronson template: 'hero-teaser-text'.
  className: PropTypes.string, // Bronson template: 'hero-teaser-modifier'.
  imgProps: PropTypes.shape({
    alt: PropTypes.string,
    sizes: PropTypes.string,
    src: PropTypes.string,
    srcSet: PropTypes.string,
  }), // Bronson template: 'srcset'.
  insideWrapper: PropTypes.bool, // Bronson template: 'hero-teaser-inside-wrapper'. Unused in Bronson-React as `imgProps` are set externally.
  noImage: PropTypes.bool, // Bronson template: 'hero-teaser-has-no-image'.
  onButtonClick: PropTypes.func,
  pretitle: PropTypes.node, // Bronson template: 'hero-teaser-pretitle'.
  sub: PropTypes.node, // Bronson template: 'hero-teaser-sub'.
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.array]), // Bronson template: 'hero-teaser-title'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  /* Convenience props. */
  auto: PropTypes.bool, // Convenience prop for `.c-hero-teaser--auto`. Bronson template: 'hero-teaser-modifier'.
  bottomCenter: PropTypes.bool, // Convenience prop for `.c-hero-teaser--bottom-center`. Bronson template: 'hero-teaser-modifier'.
  bottomLeft: PropTypes.bool, // Convenience prop for `.c-hero-teaser--bottom-left`. Bronson template: 'hero-teaser-modifier'.
  bottomRight: PropTypes.bool, // Convenience prop for `.c-hero-teaser--bottom-right`. Bronson template: 'hero-teaser-modifier'.
  centerLeft: PropTypes.bool, // Convenience prop for `.c-hero-teaser--center-left`. Bronson template: 'hero-teaser-modifier'.
  centerRight: PropTypes.bool, // Convenience prop for `.c-hero-teaser--center-right`. Bronson template: 'hero-teaser-modifier'.
  inverted: PropTypes.bool, // Convenience prop for `.c-hero-teaser--inverted`. Bronson template: 'hero-teaser-modifier'.
  shallow: PropTypes.bool, // Convenience prop for `.c-hero-teaser--shallow`. Bronson template: 'hero-teaser-modifier'.
  topCenter: PropTypes.bool, // Convenience prop for `.c-hero-teaser--top-center`. Bronson template: 'hero-teaser-modifier'.
  topLeft: PropTypes.bool, // Convenience prop for `.c-hero-teaser--top-left`. Bronson template: 'hero-teaser-modifier'.
  topRight: PropTypes.bool, // Convenience prop for `.c-hero-teaser--top-right`. Bronson template: 'hero-teaser-modifier'.
  video: PropTypes.oneOfType([PropTypes.object]), // Convenience prop for `.c-hero-teaser--video`. Bronson template: 'hero-teaser-modifier'.
}
